import { Brain, Search, GitBranch, FileText, Users, Sparkles } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, Pill } from "@/components/web/atoms";
import { Reveal } from "@/components/web/home/primitives";

/* Section 10 — Perpetual memory made tangible. Counters are clearly DEMO DATA. */
const COUNTERS = [
  { icon: Brain, label: "Your Memory", value: "86", unit: "facts", tone: "ai" },
  { icon: Search, label: "Research", value: "42", unit: "threads", tone: "ai" },
  { icon: GitBranch, label: "Decisions", value: "31", unit: "", tone: "brand" },
  { icon: FileText, label: "Documents", value: "186", unit: "", tone: "brand" },
  { icon: Users, label: "Team Knowledge", value: "2,431", unit: "items", tone: "brand" },
];

const CITES = ["Research", "Meeting", "Document", "Team Knowledge"];

export default function MemorySection() {
  return (
    <section
      id="memory"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-memory"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="ai" className="mb-4">Perpetual memory</Eyebrow>
          <SectionTitle>TeamNest remembers what your work teaches it.</SectionTitle>
          <SectionSub className="mt-5">
            Research, conversations, meetings, files and decisions become knowledge you can use again.
          </SectionSub>
        </Reveal>

        <Reveal delay={100} className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Pill tone="ai">Demo data</Pill>
            <span className="text-[12px] text-[var(--w-text-mute)]">Illustrative — not a real workspace.</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {COUNTERS.map((c) => {
              const accent = c.tone === "ai" ? "var(--w-ai)" : "var(--w-brand)";
              const tint = c.tone === "ai" ? "var(--w-ai-tint)" : "var(--w-brand-tint)";
              return (
                <div key={c.label} className="rounded-[16px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-4">
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: tint }}>
                    <c.icon className="w-4 h-4" style={{ color: accent }} />
                  </span>
                  <div className="text-[26px] font-bold tracking-[-0.02em] text-[var(--w-text)]">{c.value}</div>
                  <div className="text-[12px] text-[var(--w-text-dim)]">{c.label}{c.unit ? ` · ${c.unit}` : ""}</div>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={160} className="mt-8 max-w-3xl">
          <div className="rounded-[18px] border border-[var(--w-ai)]/30 bg-[var(--w-bg2)] p-5 sm:p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--w-ai)] mb-3">Ask TeamNest</div>
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-full bg-[var(--w-brand)] text-black text-[13px] font-bold flex items-center justify-center shrink-0">Y</div>
              <div className="rounded-[12px] rounded-tl-[4px] bg-[var(--w-surface)] border border-[var(--w-hairline)] px-3.5 py-2.5 text-[14px] text-[var(--w-text)]">
                What did we decide about pricing last quarter?
              </div>
            </div>
            <div className="mt-4 rounded-[12px] border border-[var(--w-ai)]/30 bg-[var(--w-ai-tint)]/40 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-[var(--w-ai)]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--w-ai)]">Answer · from your memory</span>
              </div>
              <p className="text-[13.5px] leading-6 text-[var(--w-text-dim)]">
                You moved Pro to seat-based pricing and kept the Free tier for individuals. The decision came out
                of the Q2 pricing review, backed by the competitor study and the finance model.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CITES.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--w-hairline)] bg-[var(--w-surface)] text-[11px] text-[var(--w-text-dim)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--w-ai)]" /> {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={220} className="mt-10 max-w-3xl">
          <p className="text-[20px] md:text-[24px] font-bold text-[var(--w-text)] leading-snug" style={{ textWrap: "balance" }}>
            Stop searching old chats, AI conversations, documents and meeting notes.
          </p>
          <p className="mt-2 text-[16px] text-[var(--w-brand)] font-semibold">
            Ask TeamNest what your work already knows.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
